import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, CreditCard, Layers, ShieldAlert, Upload, UserPlus } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useSite } from "@/context/SiteContext";
import NotFound from "@/pages/NotFound";
import { useDevAuth } from "@/context/DevAuthContext";
import { PageHero } from "@/components/layout/PageHero";
import { LongerPlanBox } from "@/components/market/Prepaid";
import { AndroidVerifyReminder, PLANS, PlanCards, SavingsBlock, SavingsCalculator, usePrice } from "@/components/market/Plans";

const STEPS = [
  { icon: UserPlus, key: "hosting.step1" },
  { icon: CreditCard, key: "hosting.step2" },
  { icon: Layers, key: "hosting.step3" },
  { icon: Upload, key: "hosting.step4" },
];

const Block = ({ icon: Icon, title, text, testId }) => (
  <div className="card-carbon rounded-2xl p-5" data-testid={testId}>
    <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white"><Icon className="h-5 w-5 text-[var(--gold)]" /> {title}</h2>
    <p className="mt-2 text-sm leading-relaxed text-neutral-300">{text}</p>
  </div>
);

export default function Hosting() {
  const { t } = useI18n();
  const { dev } = useDevAuth();
  const { marketplace } = useSite();
  const { price, k } = usePrice();
  if (!marketplace) return <NotFound />;
  const cta = dev ? "/espace-developpeur" : "/espace-developpeur/connexion?mode=signup";
  return (
    <div data-testid="hosting-page">
      <PageHero eyebrow={t("hosting.eyebrow")} title={t("hosting.title")}>
        <p className="mt-4 max-w-2xl text-sm text-neutral-300 md:text-base">{t("hosting.intro")}</p>
      </PageHero>
      <div className="mx-auto max-w-5xl space-y-8 px-4 sm:px-8">
        <PlanCards signupBase={cta} />
        <p className="text-xs text-neutral-500">{t("hosting.stripe")}</p>
        <AndroidVerifyReminder />
        <SavingsBlock />
        <SavingsCalculator />
        <LongerPlanBox testId="hosting-custom-plan" />
        <section>
          <h2 className="mb-4 font-display text-sm font-bold uppercase tracking-wider text-[var(--gold-bright)] md:text-base">{t("hosting.howTitle")}</h2>
          <ol className="grid gap-4 sm:grid-cols-2" data-testid="hosting-steps">
            {STEPS.map(({ icon: Icon, key }, i) => (
              <li key={key} className="card-carbon flex gap-4 rounded-2xl p-5" data-testid={`hosting-step-${i + 1}`}>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--gold)] font-display font-black text-black">{i + 1}</span>
                <div>
                  <p className="flex items-center gap-2 font-semibold text-white"><Icon className="h-4 w-4 text-[var(--gold)]" /> {t(`${key}.title`)}</p>
                  <p className="mt-1 text-sm text-neutral-400">{key === "hosting.step2" ? t(k(`${key}.text`), { price: price(PLANS[0]) }) : t(`${key}.text`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </section>
        <div className="grid gap-4 md:grid-cols-2">
          <Block icon={Layers} title={t("hosting.manageTitle")} text={t("hosting.manageText")} testId="hosting-manage" />
          <Block icon={CalendarClock} title={t("hosting.rulesTitle")} text={t("hosting.rulesText")} testId="hosting-rules" />
        </div>
        <Block icon={ShieldAlert} title={t("hosting.respTitle")} text={t("hosting.respText")} testId="hosting-responsibility" />
        <Link to={cta} className="btn-red btn-cta w-full sm:w-auto sm:whitespace-nowrap" data-testid="hosting-create-space-btn-bottom">
          {t("hosting.cta")} <ArrowRight className="h-4 w-4 rtl:-scale-x-100" />
        </Link>
      </div>
    </div>
  );
}
